import React, { useEffect, useState } from "react";
import { STATIC_MEDIA } from "../content/media";
import { fetchMedia } from "./api";

// Newest first; the same story (same link or slug) is only shown once, with the admin copy winning.
export const mergeMedia = (apiItems = []) => {
  const seen = new Set();
  const out = [];
  for (const item of [...apiItems, ...STATIC_MEDIA]) {
    const key = item.kind === "post" ? `post:${item.slug}` : `press:${item.url}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(item);
  }
  return out.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
};

// The site's own items show at once; admin items join when the API answers.
export const useMediaItems = () => {
  const [items, setItems] = useState(() => mergeMedia());
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    let alive = true;
    fetchMedia()
      .then((data) => alive && setItems(mergeMedia(Array.isArray(data) ? data : [])))
      .catch(() => {})
      .finally(() => alive && setLoaded(true));
    return () => {
      alive = false;
    };
  }, []);
  return { items, loaded };
};

// French when it's filled in, English otherwise.
export const localized = (item, field, lang) => (lang === "fr" && item[`${field}_fr`]) || item[field] || "";

export const formatMediaDate = (iso, lang) => {
  const [y, m, d] = String(iso).split("-").map(Number);
  if (!y || !m || !d) return iso;
  return new Intl.DateTimeFormat(lang === "fr" ? "fr-CA" : "en-CA", {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(new Date(y, m - 1, d));
};

const isExternal = (href) => /^https?:\/\//.test(href || "");
export const linkProps = (href) =>
  isExternal(href) ? { href, target: "_blank", rel: "noopener noreferrer" } : { href };

// Post bodies are plain text: blank lines between paragraphs, "## " for a subheading,
// "- " for a bullet, "> " for a pull quote (a "> — Name" line under it is the attribution).
export const PostBody = ({ text = "" }) => {
  const blocks = String(text).replace(/\r\n/g, "\n").split(/\n{2,}/).map((b) => b.trim()).filter(Boolean);
  return (
    <div className="space-y-6 text-lg text-white/80 leading-relaxed">
      {blocks.map((block, i) => {
        const lines = block.split("\n");
        if (block.startsWith("## ")) {
          return (
            <h2 key={i} className="pt-4 text-2xl md:text-3xl font-semibold tracking-tight text-white">
              {block.slice(3)}
            </h2>
          );
        }
        if (lines.every((l) => l.startsWith("- "))) {
          return (
            <ul key={i} className="space-y-3">
              {lines.map((l, j) => (
                <li key={j} className="flex gap-3">
                  <span className="mt-[0.7em] h-1.5 w-1.5 shrink-0 rounded-full bg-[#E8490F]" aria-hidden />
                  <span>{l.slice(2)}</span>
                </li>
              ))}
            </ul>
          );
        }
        if (lines.every((l) => l.startsWith(">"))) {
          const stripped = lines.map((l) => l.replace(/^>\s?/, ""));
          const cite = stripped.find((l) => /^(—|--)\s*/.test(l));
          const quote = stripped.filter((l) => l !== cite).join(" ");
          return (
            <figure key={i} className="my-4 border-l-2 border-[#E8490F] pl-6">
              <blockquote className="mise-serif mise-accent text-2xl md:text-3xl leading-snug">“{quote}”</blockquote>
              {cite && (
                <figcaption className="mt-3 text-sm text-white/50">{cite.replace(/^(—|--)\s*/, "— ")}</figcaption>
              )}
            </figure>
          );
        }
        return <p key={i}>{lines.join(" ")}</p>;
      })}
    </div>
  );
};
